import { type ReactElement } from 'react'

import {
  IconButton,
  IconMessage,
  WithBadge,
  WithPopover,
  type WithPopoverChildrenProps,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { ChatMenu, type IChatMenuProps } from './ChatMenu'
import messages from './messages'

export interface IWithChatMenuProps extends IChatMenuProps {
  /** Badge content; the caller owns the thread these belong to. */
  commentsCount: number
  unreadExternalCount?: number
  renderTrigger?: (
    commentsCount: number,
    unreadExternalCount: number
  ) => ReactElement
}

/**
 * WithChatMenu presents a ChatMenu in a popover, anchored to a badged chat
 * button unless the caller renders its own trigger.
 */
export const WithChatMenu = ({
  commentsCount,
  unreadExternalCount = 0,
  colleaguesThread,
  externalThread,
  renderTrigger,
}: IWithChatMenuProps) => {
  const intl = useProcurifyIntl()
  const hasUnreadExternal = unreadExternalCount > 0

  const renderMenu = ({ dismiss }: WithPopoverChildrenProps) => (
    <ChatMenu
      colleaguesThread={colleaguesThread}
      externalThread={{
        ...externalThread,
        onOpen: () => {
          dismiss()
          externalThread.onOpen()
        },
      }}
    />
  )

  return (
    <WithPopover trigger='click' placement='bottom' content={renderMenu}>
      <div>
        {renderTrigger ? (
          renderTrigger(commentsCount, unreadExternalCount)
        ) : (
          <WithBadge
            id='comment-count'
            show={hasUnreadExternal || commentsCount > 0}
            color={hasUnreadExternal ? 'danger' : 'primary'}
            ariaLabel={
              hasUnreadExternal
                ? intl.formatMessage(messages.unreadExternalCountAriaLabel, {
                    count: unreadExternalCount,
                  })
                : intl.formatMessage(messages.commentsCountAriaLabel, {
                    count: commentsCount,
                  })
            }
            content={hasUnreadExternal ? unreadExternalCount : commentsCount}
          >
            <IconButton
              aria-label={intl.formatMessage(messages.chatButtonAriaLabel)}
              iconComponent={<IconMessage />}
            />
          </WithBadge>
        )}
      </div>
    </WithPopover>
  )
}
